import React, { useState, useEffect } from 'react';
import classnames from 'classnames';

import { CartoonButton } from './CartoonButton';
import { CORRECT_COMBINATION, BlinkingLight } from './BlinkingLight';
import { sounds } from './GameSounds';

import 'animate.css';
import style from './LoveBox.module.less';

import device from './assets/bg.png';
import cover from './assets/cover.png';

import buttonYellowUp from './assets/button_yellow_up.png';
import buttonYellowDown from './assets/button_yellow_down.png';
import buttonBlueUp from './assets/button_blue_up.png';
import buttonBlueDown from './assets/button_blue_down.png';
import buttonGreenUp from './assets/button_green_up.png';
import buttonGreenDown from './assets/button_green_down.png';

import coverButtonOff from './assets/cover_button_off.png';
import coverButtonHover from './assets/cover_button_hover.png';
import coverButtonClick from './assets/cover_button_on.png';

import leverUp from './assets/leverOff.png';
import leverDown from './assets/leverOn.png';

import diodeGreen from './assets/diode_green.png';
import diodeBlue from './assets/diode_blue.png';
import diodeYellow from './assets/diode_yellow.png';
import glass from './assets/glass.png';

const DEFAULT_COMBINATION = ['white', 'white', 'white', 'white', 'white', 'white'];

export function preloadImages() {
  const imagesToPreload = [buttonBlueUp, buttonBlueDown, buttonGreenUp, buttonGreenDown, leverUp, leverDown, diodeGreen, diodeBlue, diodeYellow, device];
  imagesToPreload.forEach((image) => {
    const img = new Image();
    img.src = image;
  });
}

const getLight = (light) => {
  switch (light) {
    case 'green':
      return diodeGreen;
    case 'blue':
      return diodeBlue;
    case 'yellow':
      return diodeYellow;
    default:
      return null;
  }
};

interface LovePuzzleProps {
  onFinished: () => void;
}

export const LoveBox = (props: LovePuzzleProps) => {
  const [coverStatus, setCoverStatus] = useState('on');
  const [activeLight, setActiveLight] = useState(0);
  const [lights, setLights] = useState(DEFAULT_COMBINATION);
  const [pulled, setPulled] = useState(false);
  const [solved, setSolved] = useState(0);
  const [fanfare, setFanfare] = useState(false);
  const [error, setError] = useState(false);
  const [rebus, setRebus] = useState('');

  useEffect(() => preloadImages(), []);

  useEffect(() => {
    if (error) {
      setTimeout(() => setError(false), 1500);
    }
  }, [error]);

  const removeCover = () => {
    sounds.magic();
    setTimeout(() => {
      setCoverStatus('removing');
      setTimeout(() => setCoverStatus('off'), 2000);
    }, 1000);
  };

  const checkPassword = () => {
    if (rebus.toUpperCase() === 'LOVE') {
      removeCover();
    } else {
      setError(true);
      setTimeout(() => sounds.keypadError(), 200);
    }
  };

  const pushLight = (light) => {
    if (solved) {
      return;
    }

    if (CORRECT_COMBINATION[activeLight] === light) {
      const updatedLights = [...lights];
      updatedLights[activeLight] = light;
      setLights(updatedLights);

      if (activeLight === 5) {
        setSolved(1);
        setTimeout(() => setSolved(2), 900);
        sounds.powerUp();
        return;
      }

      setActiveLight(activeLight + 1);
    } else {
      setLights(DEFAULT_COMBINATION);
      setActiveLight(0);
      sounds.error();
    }
  };

  return (
    <div className={classnames(style.container, error && 'animate__animated animate__shakeX')}>
      {coverStatus === 'off' ? <></> : (
        <div
          className={style.coverContainer}
          style={{
            opacity: coverStatus === 'on' ? 1 : 0
          }}
        >
          <img src={cover} alt="cover" className={style.cover} />
          <input
            type="text"
            className={style.rebusInput}
            maxLength={4}
            placeholder="????"
            value={rebus.toUpperCase()}
            onChange={ev => setRebus(ev.target.value)}
          />
          <CartoonButton
            className={style.buttonSmall}
            src={coverButtonOff}
            hoverSrc={coverButtonHover}
            activeSrc={coverButtonClick}
            onClick={() => checkPassword()}
            onUp={() => sounds.click2()}
            onDown={() => sounds.click1()}
          />
        </div>
      )}

      <div className={style.deviceContainer}>
        <BlinkingLight solved={solved} />
        <img src={device} alt="device" className={style.device} />

        <CartoonButton
          className={style.buttonYellow}
          src={buttonYellowUp}
          hoverSrc={buttonYellowUp}
          activeSrc={buttonYellowDown}
          onClick={() => pushLight('yellow')}
          onUp={() => sounds.click4()}
          onDown={() => sounds.click5()}
        />
        <CartoonButton
          className={style.buttonBlue}
          src={buttonBlueUp}
          hoverSrc={buttonBlueUp}
          activeSrc={buttonBlueDown}
          onClick={() => pushLight('blue')}
          onUp={() => sounds.click4()}
          onDown={() => sounds.click5()}
        />
        <CartoonButton
          className={style.buttonGreen}
          src={buttonGreenUp}
          hoverSrc={buttonGreenUp}
          activeSrc={buttonGreenDown}
          onClick={() => pushLight('green')}
          onUp={() => sounds.click4()}
          onDown={() => sounds.click5()}
        />
        <CartoonButton
          className={style.lever}
          src={leverUp}
          hoverSrc={leverUp}
          activeSrc={leverDown}
          onDown={() => {
            sounds.tack();
            setPulled(true);
          }}
          onUp={() => {
            if (solved) {
              if (!fanfare) {
                sounds.fanfare();
                setFanfare(true);
                setTimeout(() => {
                  props.onFinished();
                }, 1000);
              }
            } else {
              sounds.toe();
              setPulled(false);
            }
          }}
          state={pulled ? 'down' : 'up'}
        />
        {lights.map((light, i) => {
          const img = getLight(light);
          if (img) {
            return (
              <img
                className={classnames(style.light, style.noDrag)}
                key={`light-${i}`}
                src={getLight(light)}
                alt={`light-${light}`}
                style={{ left: `${20.7 + 7.87 * i}%`, top: `${39.2 - i * 0.45}%` }}
              />
            );
          }
          return null;
        })}
        <img src={glass} alt="glass" className={classnames(style.glass, style.noDrag)} />
      </div>
    </div>
  );
};
